/**
 * Browser entry — wires F-001 through F-035 into a playable game.
 *
 * Top-level layout:
 *   1. Scene bootstrap (background, lights, playfield)
 *   2. Persistence (high score, settings)
 *   3. Audio system (settings-driven volume + muted)
 *   4. Run state (score, cooldown, queue, detector, controller, achievements)
 *   5. Visuals (launcher anchor, particle bursts, trajectory line)
 *   6. UI (score overlay, queue strip, power meter, pause badge,
 *      game-over overlay, tutorial)
 *   7. Input (slingshot, touch guard, keyboard aim)
 *   8. Game loop — pause-aware step, drain merges, evaluate game-over,
 *      render
 */

import { bootstrapScene } from "./scene/bootstrap.ts";
import { createEarth } from "./scene/earth.ts";
import { createStarfield } from "./scene/starfield.ts";
// cosmic-decorations (background planets + shooting stars) intentionally
// not mounted — backdrop is now minimalist deep space.
import { createLauncherPreview } from "./scene/launcher-preview.ts";
import { createCameraShake } from "./scene/camera-shake.ts";
import { createAtmosphereRing } from "./scene/atmosphere-ring.ts";
import { createOrbitingMoon } from "./scene/orbiting-moon.ts";
import { createAtmosphereTracker } from "./game/atmosphere.ts";
import { createEscapeDetector } from "./game/escape.ts";
import { attachIntegrityMeter } from "./ui/integrity-meter.ts";
import { createBreachWarning } from "./ui/breach-warning.ts";
import { preloadAllPlanetTextures } from "./render/planet-texture.ts";
import { createTrajectoryLine } from "./render/trajectory-line.ts";
import { createParticleBurstSystem } from "./render/particle-burst.ts";
import { createPlanetTrailSystem } from "./render/planet-trail.ts";
import { createSimulation } from "./game/spawn.ts";
import { resolveMerges } from "./game/merge.ts";
import { Score } from "./game/score.ts";
import { createCooldown } from "./game/cooldown.ts";
import { createPlanetQueue, defaultTierGenerator } from "./game/planet-queue.ts";
import { createGameOverDetector } from "./game/game-over.ts";
import { createRunController } from "./game/run-controller.ts";
import { createPauseController } from "./game/pause-controller.ts";
import { createAchievementsRegistry } from "./game/achievements.ts";
import { createAmmoController } from "./game/ammo.ts";
import { createComboTracker } from "./game/combo.ts";
import { createMergeSettle } from "./game/merge-settle.ts";
import { isStarTier } from "./game/tier.ts";
import { computeLaunchImpulse } from "./game/power.ts";
import { sampleTrajectory } from "./game/trajectory.ts";
import { attachScoreOverlay } from "./ui/score-overlay.ts";
import { attachAmmoOverlay } from "./ui/ammo-overlay.ts";
import { createQueueStrip } from "./ui/queue-strip.ts";
import { createPowerMeter } from "./ui/power-meter.ts";
import { attachPauseBadge } from "./ui/pause-badge.ts";
import { createGameOverOverlay } from "./ui/game-over-overlay.ts";
import { attachComboBadge } from "./ui/combo-badge.ts";
// 목표 텍스트는 상단 goal-pill, 사다리 칩 스트립은 하단 — 두 위젯 분리 (F-058).
import { attachGoalPill } from "./ui/goal-pill.ts";
import { attachTierLadderPanel } from "./ui/tier-ladder-panel.ts";
import { createScorePopupSystem } from "./ui/score-popup.ts";
import { createTutorialController, TUTORIAL_STEPS } from "./ui/tutorial.ts";
import { attachResponsive } from "./ui/responsive.ts";
import { mountVersionBadge } from "./ui/version-badge.ts";
import { mountAudioPrompt } from "./ui/audio-prompt.ts";
import { createSlingshot } from "./input/slingshot.ts";
import { attachTouchGuard } from "./input/touch.ts";
import { createHighScoreStore } from "./persistence/high-score-store.ts";
import { createSettingsStore } from "./persistence/settings-store.ts";
import { detectLocale, setLocale, t } from "./i18n/index.ts";
import {
  getScoreSigningKey,
  getSupabaseClient,
} from "./lib/supabase-client.ts";
import { createAuthController } from "./auth/auth-controller.ts";
import { syncProfile, updateNickname } from "./auth/profile.ts";
import { createOnlineLeaderboardStore } from "./leaderboard/online-store.ts";
import { createLeaderboardPage } from "./ui/leaderboard-page.ts";
import { createScoreSubmitDialog } from "./ui/score-submit-dialog.ts";
import { createLeaderboard } from "./persistence/leaderboard.ts";
import { createAudioSystem } from "./audio/system.ts";
import { todayUtcIso } from "./game/daily-challenge.ts";

const canvas = document.querySelector<HTMLCanvasElement>(
  '[data-testid="game-canvas"]',
);
if (!canvas) {
  throw new Error("[data-testid=game-canvas] is missing from index.html");
}
const main = document.querySelector("main");
if (!main) {
  throw new Error("<main> is missing from index.html");
}

// 1. Scene =================================================================
// F-094 — visualViewport API 가 있으면 실제 보이는 영역으로 초기화. 모바일
// 브라우저 chrome 이 펼쳐진 상태에서도 잘리지 않게.
const initialViewport = window.visualViewport
  ? { width: window.visualViewport.width, height: window.visualViewport.height }
  : { width: window.innerWidth, height: window.innerHeight };
const handles = bootstrapScene({
  canvas,
  width: initialViewport.width,
  height: initialViewport.height,
});
preloadAllPlanetTextures();

// F-041 — wider, slightly elevated framing for 3D depth. Camera tilts
// down ~12° so planets read as orbital bodies, not flat decals.
// Frame: pulled back enough that the bigger Earth (radius 4.6) on the
// right + the launch zone on the left both fit comfortably with breathing
// room. Slight elevation so the cartoon planet reads with depth.
handles.camera.position.set(0, 4, 34);
handles.camera.lookAt(0, 0, 0);
handles.camera.fov = 50;
handles.camera.updateProjectionMatrix();

// Sharper cel banding — more directional contrast, lower ambient
// floor. Pure-step gradient (no easing) plus this lighting balance
// produces a clear lit/shadow split on every body.
handles.lights.ambient.intensity = 0.40;
handles.lights.sun.intensity = 1.55;

// Beautiful rotating Earth on the right side. Visible gravity source +
// solid surface meteors collide with and stack on.
const EARTH_POSITION = { x: 14, y: 0, z: 0 };
const earth = createEarth({ position: EARTH_POSITION });
handles.scene.add(earth.group);
// (Earth uses MeshToonMaterial — lit by handles.lights.sun directly.)

// Visible atmosphere boundary — debris that drifts past this radius and
// settles there counts as a breach. Each breach deals tier-scaled
// damage to Earth's structural integrity; full collapse = game over.
// Offset history: 4.2 → 4.8 (F-? buffer up) → 4.7 (F-100, slight
// difficulty bump — breach line a touch closer to Earth).
const ATMOSPHERE_RADIUS = earth.radius + 4.7;
const atmosphereRing = createAtmosphereRing({
  earthRadius: earth.radius,
  atmosphereRadius: ATMOSPHERE_RADIUS,
});
atmosphereRing.mesh.position.set(EARTH_POSITION.x, EARTH_POSITION.y, EARTH_POSITION.z);
handles.scene.add(atmosphereRing.mesh);

// 달 — Earth 를 천천히 도는 궤도 장애물. 운석이 부딪히면 튕긴다.
const moon = createOrbitingMoon({
  center: EARTH_POSITION,
  initialAngle: Math.PI * 0.3,
});
handles.scene.add(moon.group);

// F-039 — instanced point starfield. Sits inside the gradient sphere.
const starfield = createStarfield();
handles.scene.add(starfield.object);

// (Background planets + shooting stars removed — F-052 simplified the
// backdrop. The flat cel-banded dome + 3D starfield carry the cosmic
// feel without busy decorations competing with the play action.)

// 2. Persistence ===========================================================
const highScoreStore = createHighScoreStore();
const settingsStore = createSettingsStore();
const leaderboard = createLeaderboard();

// Pre-resolve the online-mode flag so UI code constructed BEFORE the
// onlineStore is wired (e.g. game-over-overlay) can branch on it.
const onlineModeAvailable =
  Boolean(getSupabaseClient()) && Boolean(getScoreSigningKey());

// 2b. Locale — set before any UI component renders so first paint is in
// the player's language. Manual override (settings.locale) wins over the
// browser auto-detect.
setLocale(settingsStore.current().locale ?? detectLocale());

// 2c. Run start timestamp — used by score-submit to compute duration_ms.
const runStartedAt = { current: Date.now() };

// 3. Audio =================================================================
const audio = createAudioSystem({
  initialVolume: settingsStore.current().volume,
  initialMuted: settingsStore.current().muted,
});
settingsStore.subscribe((s) => {
  audio.setVolume(s.volume);
  audio.setMuted(s.muted);
});

// 4. Run state =============================================================
const score = new Score();
score.setHighScore(highScoreStore.load());

const cooldown = createCooldown();
const queue = createPlanetQueue({
  lookahead: 3,
  generator: defaultTierGenerator(),
});
// F-037: limit-line game-over no longer applies (no top-stack concept
// in the side-on Sun layout). Park the detector at an unreachable y so
// it never fires; F-040 introduces the ammo-based game over.
const detector = createGameOverDetector({
  limitLineY: 999,
});
const pause = createPauseController();
const achievements = createAchievementsRegistry();
// Ammo controller is retained for the achievement/daily-challenge code
// paths but is no longer a game-over trigger — the new rule is the
// atmosphere breach system below. We give it a very high cap so suika-style
// unlimited play is the effective UX.
const ammo = createAmmoController({ total: 9999 });
const combo = createComboTracker();
// Post-merge velocity stabilizer — keeps freshly-merged bodies from
// springing wildly upward when cannon-es resolves their spawn overlap.
const mergeSettle = createMergeSettle();
const atmosphere = createAtmosphereTracker({
  atmosphereRadius: ATMOSPHERE_RADIUS,
  // 100-point structural integrity — paired with tier-scaled damage so
  // bigger meteors drain Earth faster (default damageForTier).
});

// Orbit escape — far past the atmosphere, the meteor is effectively
// off-screen and counts as a separate "lost asset" event (not a
// boundary breach). Radius chosen so the launch origin (distance ~30
// from Earth) is comfortably inside, while truly off-screen flights
// still trip the detector.
const ESCAPE_RADIUS = 45;
const escape = createEscapeDetector({ escapeRadius: ESCAPE_RADIUS });
achievements.attachToScore(score);

// Gravity well centered on Earth — strong pull so meteors actually
// arc into the planet from across the playfield.
const sim = createSimulation({
  scene: handles.scene,
  well: {
    center: EARTH_POSITION,
    strength: 320,
    maxRadius: 80,
    minDistance: earth.radius + 0.5,
  },
});

// Add Earth's static body to the physics world so meteors collide with
// (and stack on) its surface rather than passing through.
sim.world.add(earth.body);
// Moon's kinematic body — meteors bounce off naturally via cannon.
sim.world.add(moon.body);
const runController = createRunController({
  sim,
  score,
  cooldown,
  queue,
  detector,
  reseedQueue: () => queue.reseed(defaultTierGenerator()),
});

// 5. Visuals ===============================================================
// Visible "next ammo" preview at the slingshot origin. Without this,
// players couldn't see what they were about to fire — the launch zone
// looked empty.
const LAUNCH_ORIGIN = { x: -16, y: 0, z: 0 };
const launcherPreview = createLauncherPreview({ origin: LAUNCH_ORIGIN });
handles.scene.add(launcherPreview.group);
const launcher = {
  origin: LAUNCH_ORIGIN,
  setNextTier: (tier: number): void => launcherPreview.setNextTier(tier),
  reload: (tier: number): void => launcherPreview.reload(tier),
  setPullOffset: (offset: { x: number; y: number }): void =>
    launcherPreview.setPullOffset(offset),
  setVisible: (visible: boolean): void => launcherPreview.setVisible(visible),
};
launcher.setNextTier(queue.peek()[0] ?? 0);

const particles = createParticleBurstSystem({ scene: handles.scene });
const trajectory = createTrajectoryLine({ scene: handles.scene });
const trails = createPlanetTrailSystem({ scene: handles.scene });

// Cel spark burst on every meteor-meteor contact — including
// cross-tier bounces where no merge happens. Gives the player a
// satisfying "thump" cue.
sim.collisions.onImpact((impact) => {
  // Skip merge-tier same-pair impacts: those already get the bigger
  // merge burst from the resolveMerges path. Spark is for everything
  // else — bounces, glances, large-vs-small contacts.
  if (impact.isMerge) return;
  const intensity = Math.min(1.5, 0.5 + impact.speed * 0.05);
  particles.spawnSpark({
    position: impact.point,
    color: impact.primary.tier.color,
    intensity,
  });
});

// Earth structural-stress reactions — each breach scales with the
// breaching meteor's tier (bigger tier = more stress + bigger FX).
atmosphere.subscribeBreach((breach) => {
  atmosphereRing.flashBreach();
  breachWarning.flashBreach();
  // Camera kick scales with damage so heavy hits feel heavy.
  const kick = 0.5 + breach.damage * 0.04;
  cameraShake.kick(Math.min(1.6, kick));
  integrityMeter.punchLoss();
  const meteor = sim.planets.find((p) => p.id === breach.meteorId);
  if (meteor) {
    const sparkCount = 3 + Math.floor(breach.damage / 4);
    for (let i = 0; i < sparkCount; i += 1) {
      particles.spawnSpark({
        position: {
          x: meteor.body.position.x,
          y: meteor.body.position.y,
          z: meteor.body.position.z,
        },
        color: 0xff5b5b,
        intensity: Math.min(1.8, 0.9 + breach.damage * 0.04),
      });
    }
  }
  audio.play("gameover", { intensity: 0.4 });
});

// Pipe integrity ratio into Earth visuals — atmosphere color shifts
// + planet wobble intensifies as the world approaches collapse.
atmosphere.subscribeIntegrity((s) => {
  earth.setStress(s.current / s.max);
});

// 궤도 이탈 (orbit escape) — flash distinct banner + camera kick +
// small spark. F-101: 지구 내구도는 깎지 않고 (운석이 GONE — 더 이상
// 표면에 압력을 주지 않음), 점수에만 작은 패널티. 잃어버린 자산에
// 대한 비용. tier 가 클수록 더 큰 손실 — 큰 운석을 놓치는 게 더 아픔.
// Tuning history: F-101 v0.2.7 = 10 + tier×3 (-10 ~ -34) → 사용자 피드백
// 너무 큼 → 1/3 으로 축소 (-3 ~ -11). 소폭 패널티 유지.
const ESCAPE_PENALTY_BASE = 3;
const ESCAPE_PENALTY_PER_TIER = 1;
escape.subscribe((event) => {
  breachWarning.flashEscape();
  cameraShake.kick(0.55);
  audio.play("gameover", { intensity: 0.25 });
  const penalty = ESCAPE_PENALTY_BASE + event.tierIndex * ESCAPE_PENALTY_PER_TIER;
  score.penalize(penalty);
  // Light cyan sparks + 빨간 -N popup at the last known meteor position.
  const meteor = sim.planets.find((p) => p.id === event.meteorId);
  if (meteor) {
    const pos = {
      x: meteor.body.position.x,
      y: meteor.body.position.y,
      z: meteor.body.position.z,
    };
    particles.spawnSpark({
      position: pos,
      color: 0x6ec4ff,
      intensity: 1.0,
    });
    scorePopups.spawn({
      position: pos,
      label: `-${penalty}`,
      color: 0xff7878,
    });
  }
});

// Detect meteor → Moon contact and flash a silver spark — the moon is
// an obstacle so the player needs visual feedback when their shot
// glances off it.
moon.body.addEventListener("collide", (e: { body: { id: number } }) => {
  const meteor = sim.planets.find((p) => p.body.id === e.body.id);
  if (!meteor) return;
  const speed = meteor.body.velocity.length();
  if (speed < 0.8) return;
  particles.spawnSpark({
    position: {
      x: meteor.body.position.x,
      y: meteor.body.position.y,
      z: meteor.body.position.z,
    },
    color: 0xc0bcd0,
    intensity: Math.min(1.4, 0.7 + speed * 0.04),
  });
});

// Detect meteor → Earth contact directly off Earth's static body so we
// also flash a spark when a meteor lands on the planet surface.
earth.body.addEventListener("collide", (e: { body: { id: number } }) => {
  const meteor = sim.planets.find((p) => p.body.id === e.body.id);
  if (!meteor) return;
  const speed = meteor.body.velocity.length();
  if (speed < 1.5) return; // ignore gentle resting contacts
  particles.spawnSpark({
    position: {
      x: meteor.body.position.x,
      y: meteor.body.position.y,
      z: meteor.body.position.z,
    },
    color: meteor.tier.color,
    intensity: Math.min(1.5, 0.6 + speed * 0.04),
  });
});

// 6. UI ====================================================================
const scoreOverlay = attachScoreOverlay(score, main);
const integrityMeter = attachIntegrityMeter(atmosphere, main);
const queueStrip = createQueueStrip(queue, main);
const powerMeter = createPowerMeter(main);
const pauseBadge = attachPauseBadge(pause, main);
void attachAmmoOverlay; // legacy export retained for tests; HUD no longer mounts it
void integrityMeter;
let endlessMode = false;
// F-086 — track this run's max tier for leaderboard "도달 단계" display.
// Reset on restart; updated whenever a higher tier is forged.
let peakTierThisRun = 0;
const goalPill = attachGoalPill(main);
const tierLadderPanel = attachTierLadderPanel(main);
const gameOverOverlay = createGameOverOverlay(main, {
  onRestart: () => {
    trails.clear();
    runController.restart();
    detector.reset();
    ammo.reset();
    combo.reset();
    atmosphere.reset();
    endlessMode = false;
    peakTierThisRun = 0;
    mergeSettle.reset();
    escape.reset();
    goalPill.reset();
    tierLadderPanel.reset();
    launcher.setVisible(true);
    launcher.setPullOffset({ x: 0, y: 0 });
    launcher.setNextTier(queue.peek()[0] ?? 0);
    queueStrip.refresh();
    gameOverOverlay.hide();
    runStartedAt.current = Date.now();
  },
  onContinue: () => {
    // Victory acknowledged — keep the run going for high-score chasing.
    gameOverOverlay.hide();
    launcher.setVisible(true);
  },
  // F-076 — explicit submit entry. Wired only when online mode is
  // available. The button shows up on the run-end overlay so the user
  // can register any non-zero score, not just new personal bests.
  ...(onlineModeAvailable
    ? {
        onSubmit: () => {
          const snap = score.snapshot();
          if (snap.current <= 0) return;
          maybePromptSubmit(snap, endlessMode, true);
        },
      }
    : {}),
});

const comboBadge = attachComboBadge(combo, main);
const scorePopups = createScorePopupSystem({ host: main, camera: handles.camera });
const cameraShake = createCameraShake({ camera: handles.camera });
const breachWarning = createBreachWarning({ host: main, camera: handles.camera });
void comboBadge;

const tutorial = createTutorialController();
const tutorialOverlay = mountTutorialOverlay(main, tutorial, {
  // F-079 — freeze the sim while the user reads the tutorial; resume on
  // dismiss (next-end or skip) so play starts immediately.
  onShown: () => pause.pause(),
  onDismissed: () => pause.resume(),
});

// =========================================================================
// 4b. F-073 — Auth + global leaderboard wiring.
// =========================================================================
const supabaseClient = getSupabaseClient();
const signingKey = getScoreSigningKey();
const authController = createAuthController(supabaseClient);
// F-081 — hash cleanup moved INTO createAuthController so it fires
// AFTER supabase-js has consumed the callback hash. Calling it here
// (boot-time, before INITIAL_SESSION) raced with detectSessionInUrl
// and silently killed every new OAuth session.
const onlineStore =
  supabaseClient && signingKey
    ? createOnlineLeaderboardStore({
        client: supabaseClient,
        signingKey,
      })
    : null;

async function refreshLeaderboard(forceRefresh = false): Promise<void> {
  if (!onlineStore) {
    leaderboardPage.setStatus("offline");
    leaderboardPage.setRows([]);
    return;
  }
  leaderboardPage.setStatus("loading");
  try {
    const rows = await onlineStore.fetchTop(1000, { forceRefresh });
    leaderboardPage.setRows(rows);
    leaderboardPage.setStatus("online");
    const user = authController.getUser();
    if (user) {
      const userRow = await onlineStore.fetchUserRank(user.id, { forceRefresh });
      leaderboardPage.setUserHighlight(userRow);
    }
  } catch (err) {
    console.error("[leaderboard]", err);
    leaderboardPage.setStatus("error");
  }
}

const leaderboardPage = createLeaderboardPage(main, {
  authController,
  onClose: () => leaderboardPage.hide(),
  // Refresh button bypasses the 5-min cache — user explicitly asked.
  onRefresh: () => void refreshLeaderboard(true),
  onError: (err) => console.error("[auth]", err),
  // F-078 — nickname inline editor. Wired only when client + user
  // present (i.e. online + signed in). The chip's edit button is
  // hidden in any other state.
  ...(supabaseClient
    ? {
        onNicknameSave: async (nickname: string) => {
          const user = authController.getUser();
          if (!user) return { ok: false, reason: "not_signed_in" };
          const result = await updateNickname(
            supabaseClient,
            user.id,
            nickname,
          );
          if (result.ok) {
            // F-082 — push the saved nickname onto the auth controller so
            // the submit dialog / profile chip / any other consumer sees
            // the updated displayName immediately (no reload required).
            authController.setNickname(result.nickname ?? null);
            // Cache invalidate so the leaderboard re-fetches the new
            // display_name on the next open.
            onlineStore?.invalidateCache();
            void refreshLeaderboard(true);
            return { ok: true, nickname: result.nickname };
          }
          return { ok: false, reason: result.reason };
        },
      }
    : {}),
});

const submitDialog = createScoreSubmitDialog(main, {
  authController,
  store: onlineStore,
  onSubmitted: (rank) => {
    leaderboardPage.show();
    void refreshLeaderboard();
    void rank;
  },
  onError: (err) => console.error("[submit]", err),
});

// Sync the profile row right after Google sign-in so the leaderboard
// has the up-to-date name + avatar before the user submits anything.
// F-082 — also fetch any user-set nickname back onto the auth controller
// so the displayName seen across the UI matches what the leaderboard
// shows (no Google-vs-nickname mismatch on the submit dialog).
{
  let nicknameLoadedFor: string | null = null;
  authController.subscribe((user) => {
    if (!user || !supabaseClient) {
      nicknameLoadedFor = null;
      return;
    }
    void syncProfile(supabaseClient, user).catch((err) =>
      console.error("[profile-sync]", err),
    );
    if (nicknameLoadedFor === user.id) return; // already fetched this session
    nicknameLoadedFor = user.id;
    void supabaseClient
      .from("profiles")
      .select("display_name")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        const saved = (data?.display_name ?? "").trim();
        if (saved && saved !== user.displayName) {
          authController.setNickname(saved);
        }
      });
  });
}

function openLeaderboard(): void {
  leaderboardPage.show();
  void refreshLeaderboard();
}

// Open leaderboard via 'L' hotkey (desktop) — easy keyboard discoverability.
window.addEventListener("keydown", (e) => {
  if (e.key === "l" || e.key === "L") {
    if (e.target instanceof HTMLElement && e.target.tagName === "BUTTON") return;
    openLeaderboard();
  }
});

// F-075 — score-overlay 자체가 leaderboard 진입점. 게임 화면에는 별도
// 버튼 없음 — UI 가 깨끗해진다.
scoreOverlay.element.addEventListener("click", openLeaderboard);
scoreOverlay.element.addEventListener("keydown", (e) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    openLeaderboard();
  }
});

// Prompt the submission dialog only when (a) we have an online store
// configured AND (b) the run is meaningful (star forged or new high
// score). Repeat losses below the local high don't pester the player.
function maybePromptSubmit(
  snapshot: ReturnType<typeof score.snapshot>,
  starForged: boolean,
  force = false,
): void {
  if (!onlineStore) return;
  if (snapshot.current <= 0) return;
  // Auto-flow only prompts on a meaningful run (star or new high). The
  // explicit 🏆 등록 button (F-076) bypasses with force=true.
  if (!force && !starForged && snapshot.current <= snapshot.highScore) return;
  const durationMs = Math.max(30_000, Date.now() - runStartedAt.current);
  // F-086 — coerce peakTier to be consistent with starForged so the
  // server's sanity check passes (star_forged ⇔ peak_tier === 8).
  const peakTier = starForged
    ? 8
    : Math.max(0, Math.min(7, peakTierThisRun));
  submitDialog.prompt({
    userId: authController.getUser()?.id ?? "anonymous",
    score: snapshot.current,
    merges: snapshot.merges,
    durationMs,
    starForged,
    peakTier,
  });
}

void pauseBadge;

// Earth's emotion driver — reflects ammo + biggest in-flight tier so
// the player feels rising tension. Thresholds intentionally low so
// emotions shift through several states across a normal run rather
// than staying stuck on calm.
function syncEarthEmotion(): void {
  if (runController.isGameOver()) {
    earth.setEmotion("scared");
    return;
  }
  const a = ammo.state();
  const ratio = a.remaining / Math.max(1, a.total);
  const maxTier = sim.planets.reduce(
    (m, p) => Math.max(m, p.tier.index),
    0,
  );
  // Order matters — higher-priority states win.
  if (maxTier >= 8 || (ratio < 0.15 && maxTier >= 4)) {
    earth.setEmotion("scared");
  } else if (maxTier >= 6 || ratio < 0.25) {
    earth.setEmotion("worried");
  } else if (maxTier >= 3 || ratio < 0.55) {
    earth.setEmotion("nervous");
  } else if (ratio < 0.90 || maxTier >= 1) {
    earth.setEmotion("happy");
  } else {
    earth.setEmotion("calm");
  }
}

// 7. Input =================================================================
const PULL_VISUAL_SCALE = 1.6; // world units per normalized power
const slingshot = createSlingshot(canvas);
attachTouchGuard(canvas);

slingshot.on("drag", (aim) => {
  if (!aim.active || pause.isPaused() || runController.isGameOver()) return;
  if (!cooldown.isReady()) return;

  // Visual: pull held planet back, show power meter, draw trajectory.
  const pullX = aim.direction.x * aim.power * PULL_VISUAL_SCALE;
  const pullY = -aim.direction.y * aim.power * PULL_VISUAL_SCALE;
  launcher.setPullOffset({ x: pullX, y: pullY });
  powerMeter.setVisible(true);
  powerMeter.setPower(aim.power);

  if (aim.power > 0.04) {
    // Tame the impulse so meteors don't fly past Earth — gravity does
    // most of the work guiding them in.
    const impulse = computeLaunchImpulse(
      aim.power * settingsStore.current().sensitivity,
    ) * 0.55;
    // Pull-back semantic — drag in OPPOSITE of launch direction (Angry
    // Birds style). To launch right toward Earth, drag the planet LEFT.
    const velocity = {
      x: aim.direction.x * impulse,
      y: -aim.direction.y * impulse,
      z: 0,
    };
    const samples = sampleTrajectory({
      origin: launcher.origin,
      velocity,
      well: sim.well,
      steps: 32,
      dt: 0.07,
      maxRadius: 30,
    });
    trajectory.setPoints(samples);
    trajectory.setVisible(true);
  } else {
    trajectory.setVisible(false);
  }
});

slingshot.on("release", (aim) => {
  trajectory.setVisible(false);
  powerMeter.setVisible(false);
  launcher.setPullOffset({ x: 0, y: 0 });

  if (pause.isPaused() || runController.isGameOver()) return;
  if (aim.power < 0.05) return;
  if (ammo.isEmpty()) return;
  if (!cooldown.tryArm()) return;
  ammo.consume();

  const tierIndex = queue.advance();
  // Match the trajectory-preview impulse (× 0.55) so the dotted line
  // truly predicts where the meteor goes.
  const impulse = computeLaunchImpulse(
    aim.power * settingsStore.current().sensitivity,
  ) * 0.55;
  // Pull-back semantic — match the drag handler.
  const planet = sim.spawnPlanet({
    tierIndex,
    position: { ...launcher.origin },
    velocity: {
      x: aim.direction.x * impulse,
      y: -aim.direction.y * impulse,
      z: 0,
    },
  });
  trails.track(planet);
  audio.play("launch", { intensity: aim.power });
  // First time the player commits a tier into play, light it up on
  // both the goal-pill peak counter and the bottom tier-ladder chip.
  goalPill.setPeakTier(planet.tier.index);
  tierLadderPanel.setPeakTier(planet.tier.index);

  // Muzzle flash — bright spark burst at the launch zone for the
  // "round leaves the chamber" punctuation.
  particles.spawnSpark({
    position: { ...launcher.origin },
    color: planet.tier.color,
    intensity: 1.4,
  });

  // Reload sequence — preview vanishes briefly, then drops the next
  // tier in. Player visually understands "ammo cycled".
  launcher.reload(queue.peek()[0] ?? 0);
  queueStrip.refresh();
});

slingshot.on("cancel", () => {
  trajectory.setVisible(false);
  powerMeter.setVisible(false);
  launcher.setPullOffset({ x: 0, y: 0 });
});

// Keyboard a11y — Esc pauses, Enter restarts on game-over screen.
window.addEventListener("keydown", (event) => {
  if (event.code === "Escape") {
    pause.toggle();
  }
  if (
    event.code === "Enter" &&
    runController.isGameOver()
  ) {
    gameOverOverlay.element.querySelector<HTMLButtonElement>(
      '[data-testid="gameover-restart"]',
    )?.click();
  }
});

// Tutorial — show on first visit
if (tutorial.shouldShow()) {
  tutorial.show();
  tutorialOverlay.show();
}

// Responsive resize
attachResponsive({
  camera: handles.camera,
  renderer: handles.renderer,
  collapsiblePanels: [queueStrip.element],
});

// F-092 — 우하단 버전 뱃지. tap 시 토스트에 audio 시스템 진단 함께 표시
// (F-091 hotfix#3 — iPad 사운드 무음 디버깅용).
mountVersionBadge({
  diagnose: () => {
    const d = audio.diagnostics();
    return [
      `audio.ctx ${d.ctxState}`,
      d.sampleRate ? `sr ${d.sampleRate}` : null,
      `media-promoted ${d.mediaSessionPromoted ? "yes" : "no"}`,
      d.audioSessionType ? `session ${d.audioSessionType}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  },
});

// F-091 hotfix#3 — explicit "tap to enable sound" pill. 자동 unlock 이
// iPad 에서 실패한 경우의 보장 path. ctx 가 'running' 으로 확인되면
// 자동으로 사라짐.
mountAudioPrompt({ audio });

// 8. Game loop =============================================================
let lastT = performance.now();
function tick(now: number): void {
  const dt = Math.min((now - lastT) / 1000, 1 / 30);
  lastT = now;

  if (!pause.isPaused() && !runController.isGameOver()) {
    sim.step(dt);

    // Constrain every meteor to the camera-facing playfield plane (z=0).
    // Without this they orbit around behind Earth and disappear from view.
    for (const p of sim.planets) {
      p.body.position.z = 0;
      p.body.velocity.z = 0;
    }

    // Resolve merges → score, audio, particles, combo, shake, popups
    const candidates = sim.drainMergeCandidates();
    if (candidates.length > 0) {
      // Snapshot the merge centroids BEFORE resolution removes the bodies.
      const mergeCenters = candidates.map((c) => ({
        x: (c.a.body.position.x + c.b.body.position.x) / 2,
        y: (c.a.body.position.y + c.b.body.position.y) / 2,
        z: (c.a.body.position.z + c.b.body.position.z) / 2,
        tierIndex: c.tierIndex,
      }));
      // Forget trails of the planets about to be consumed by the merge.
      for (const c of candidates) {
        trails.forget(c.a);
        trails.forget(c.b);
      }
      const result = resolveMerges(sim, candidates);
      // Combo: each merge in this batch extends the chain. The first
      // batch-internal merge gets fresh state; subsequent batch entries
      // also extend (effectively "multi-merge in one frame").
      let multiplier = 1;
      for (let i = 0; i < result.ascensions + result.topTierBursts; i += 1) {
        multiplier = combo.registerMerge(now);
      }
      if (result.scoreDelta > 0) {
        const boosted = Math.round(result.scoreDelta * multiplier);
        score.add(boosted, result.ascensions + result.topTierBursts);
        // Floating "+points" popup at the first merge center — tinted
        // by the resulting tier color.
        const center = mergeCenters[0];
        if (center) {
          const tierColor =
            result.spawned[0]?.tier.color ?? 0xffffff;
          scorePopups.spawn({
            position: { x: center.x, y: center.y, z: center.z },
            points: boosted,
            color: tierColor,
            ...(multiplier > 1 ? { multiplier } : {}),
          });
        }
      }
      // Camera shake — intensity scales with the biggest merge tier.
      const peakTier = mergeCenters.reduce(
        (m, c) => Math.max(m, c.tierIndex),
        0,
      );
      cameraShake.kick(0.25 + peakTier * 0.08);
      for (const planet of result.spawned) {
        const intensity = Math.min(1, planet.tier.index / 6);
        particles.spawn({
          position: {
            x: planet.body.position.x,
            y: planet.body.position.y,
            z: planet.body.position.z,
          },
          color: planet.tier.color,
        });
        audio.play("merge", { intensity });
        trails.track(planet);
        goalPill.setPeakTier(planet.tier.index);
        tierLadderPanel.setPeakTier(planet.tier.index);
        if (planet.tier.index > peakTierThisRun) {
          peakTierThisRun = planet.tier.index;
        }
        // Tame the post-merge spawn impulse for ~250ms so the new
        // (bigger) body doesn't spring wildly when cannon-es resolves
        // its overlap with the surrounding stack.
        mergeSettle.register(planet.id, planet.body, planet.tier.index, now);
      }
    }
    combo.tick(now);
    mergeSettle.tick(now);

    // Victory — first STAR forged unlocks endless mode. Show the
    // explicit "🌟 STAR FORGED" overlay so the goal-met moment is
    // unambiguous; player clicks Continue to keep playing for score.
    const hasStar = sim.planets.some((p) => isStarTier(p.tier.index));
    if (hasStar && !endlessMode && !runController.isGameOver()) {
      endlessMode = true;
      const snapshot = score.snapshot();
      if (snapshot.current > snapshot.highScore) {
        score.setHighScore(snapshot.current);
        highScoreStore.save(snapshot.current);
      }
      audio.play("merge", { intensity: 1 });
      cameraShake.kick(1.4);
      for (let burst = 0; burst < 14; burst += 1) {
        particles.spawn({
          position: {
            x: EARTH_POSITION.x + (Math.random() - 0.5) * earth.radius,
            y: EARTH_POSITION.y + (Math.random() - 0.5) * earth.radius,
            z: EARTH_POSITION.z,
          },
          color: 0xffe066,
        });
      }
      // Restore Earth integrity — clean slate for endless run.
      atmosphere.reset();
      earth.setEmotion("celebrate");
      gameOverOverlay.show(score.snapshot(), "star-forged");
      maybePromptSubmit(score.snapshot(), true);
    }

    // Per-frame atmosphere + escape update. Build snapshots once with each
    // meteor's distance from Earth, speed, tier.
    const meteorSnapshots = sim.planets.map((p) => {
      const dx = p.body.position.x - EARTH_POSITION.x;
      const dy = p.body.position.y - EARTH_POSITION.y;
      const dz = p.body.position.z - EARTH_POSITION.z;
      return {
        id: p.id,
        distance: Math.hypot(dx, dy, dz),
        speed: p.body.velocity.length(),
        tierIndex: p.tier.index,
      };
    });

    // Escape first — meteors that flew off-screen are removed from
    // the sim BEFORE the atmosphere breach detector runs, so they can't
    // double-fire as both "escape" and "breach".
    const escapedIds = escape.update(meteorSnapshots);
    if (escapedIds.length > 0) {
      const escapedSet = new Set(escapedIds);
      for (const id of escapedIds) {
        const p = sim.planets.find((pl) => pl.id === id);
        if (p) sim.removePlanet(p);
      }
      // Drop their snapshots so atmosphere doesn't see them this frame.
      for (let i = meteorSnapshots.length - 1; i >= 0; i -= 1) {
        if (escapedSet.has(meteorSnapshots[i]!.id)) {
          meteorSnapshots.splice(i, 1);
        }
      }
    }

    atmosphere.update(dt, meteorSnapshots);

    // Drive the ring's danger gauge — closer-to-breach meteors raise
    // the warning hue. We use the count of dwelling meteors as a
    // simple proxy: any in-flight dwell shows yellow; multiple show
    // full red.
    const dwellingIds = atmosphere.dwellingMeteorIds();
    atmosphereRing.setDanger(Math.min(1, dwellingIds.length * 0.55));

    // Per-meteor "DANGER" warning label on every meteor that's
    // currently dwelling outside the atmosphere — the explicit cue that
    // this meteor is the one counting down to a breach.
    const idSet = new Set(dwellingIds);
    const dwellingMeteors = sim.planets
      .filter((p) => idSet.has(p.id))
      .map((p) => ({
        id: p.id,
        position: {
          x: p.body.position.x,
          y: p.body.position.y,
          z: p.body.position.z,
        },
      }));
    breachWarning.setDwellingMeteors(dwellingMeteors, now, {
      width: handles.renderer.domElement.clientWidth,
      height: handles.renderer.domElement.clientHeight,
    });

    // Game-over (loss) — Earth structural integrity collapsed.
    if (atmosphere.isCollapsed() && !runController.isGameOver()) {
      const snapshot = score.snapshot();
      if (snapshot.current > snapshot.highScore) {
        score.setHighScore(snapshot.current);
        highScoreStore.save(snapshot.current);
      } else {
        highScoreStore.save(snapshot.highScore);
      }
      leaderboard.record({
        score: snapshot.current,
        merges: snapshot.merges,
        iso: todayUtcIso(),
      });
      achievements.evaluate({ score: snapshot, gameOver: true });
      audio.play("gameover");
      runController.endRun();
      gameOverOverlay.show(score.snapshot(), "shattered");
      launcher.setVisible(false);
      maybePromptSubmit(score.snapshot(), false);
    }
  }

  earth.tick(dt);
  moon.tick(dt);
  syncEarthEmotion();
  starfield.tick(dt);
  launcherPreview.tick(now);
  trails.tick();
  particles.tick(now);
  atmosphereRing.tick(dt);
  cameraShake.tick(dt);
  comboBadge.tick(now);
  scorePopups.tick(now, {
    width: handles.renderer.domElement.clientWidth,
    height: handles.renderer.domElement.clientHeight,
  });
  // Backdrop is now a flat plane — no rotation applied.
  handles.renderer.render(handles.scene, handles.camera);
  requestAnimationFrame(tick);
}

requestAnimationFrame(tick);

// Test seam (F-036) — minimal hook for the gameplay smoke. Production
// builds tree-shake this when `import.meta.env.PROD` is true via Vite,
// but happy to keep it lightweight in dev.
declare global {
  interface Window {
    __cosmic?: {
      planets(): number;
      score(): { current: number; high: number; merges: number };
      ammo(): { remaining: number; total: number };
      gameOver(): boolean;
    };
  }
}
window.__cosmic = {
  planets: () => sim.planets.length,
  score: () => {
    const s = score.snapshot();
    return { current: s.current, high: s.highScore, merges: s.merges };
  },
  ammo: () => ammo.state(),
  gameOver: () => runController.isGameOver(),
};

// =========================================================================
// Tutorial overlay — minimal inline mount. Decoupled from src/ui/tutorial.ts
// (which owns state) so the visual layer can stay light.
// =========================================================================
interface TutorialOverlayHandle {
  show(): void;
}

interface TutorialOverlayOptions {
  /** Fired the first time render() actually displays the card. */
  onShown?: () => void;
  /** Fired when the card hides (next-end OR skip). */
  onDismissed?: () => void;
}

function mountTutorialOverlay(
  host: HTMLElement,
  controller: ReturnType<typeof createTutorialController>,
  options: TutorialOverlayOptions = {},
): TutorialOverlayHandle {
  const totalSteps = TUTORIAL_STEPS.length;
  const wrap = document.createElement("div");
  wrap.dataset["testid"] = "tutorial-overlay";
  wrap.className =
    "pointer-events-auto absolute inset-x-0 bottom-32 mx-auto flex w-[min(440px,92vw)] flex-col gap-2 rounded-2xl bg-black/75 p-4 text-slate-100 shadow-2xl ring-1 ring-white/10 backdrop-blur-md transition-opacity duration-200 sm:bottom-40";
  wrap.style.display = "none";

  const header = document.createElement("div");
  header.className = "flex items-baseline justify-between gap-3";

  const title = document.createElement("div");
  title.dataset["testid"] = "tutorial-title";
  title.className = "font-display text-base text-cosmic-star sm:text-lg";

  const stepCount = document.createElement("div");
  stepCount.dataset["testid"] = "tutorial-step-count";
  stepCount.className = "text-[10px] tracking-widest text-slate-400 sm:text-xs";

  header.append(title, stepCount);

  const body = document.createElement("p");
  body.className = "text-sm leading-snug text-slate-200";

  const actions = document.createElement("div");
  actions.className = "flex justify-end gap-2 pt-2";
  const skip = document.createElement("button");
  skip.type = "button";
  skip.dataset["testid"] = "tutorial-skip";
  skip.textContent = t("tutorial.skip");
  skip.className =
    "rounded-md px-3 py-1 text-xs text-slate-300 hover:bg-white/10";
  const next = document.createElement("button");
  next.type = "button";
  next.dataset["testid"] = "tutorial-next";
  next.textContent = t("tutorial.next");
  next.className =
    "rounded-md bg-cosmic-glow/80 px-4 py-1 text-xs font-bold text-cosmic-deep hover:bg-cosmic-glow";
  actions.append(skip, next);

  wrap.append(header, body, actions);
  host.appendChild(wrap);

  const findStepIndex = (stepId: string | undefined): number => {
    if (!stepId) return -1;
    return TUTORIAL_STEPS.findIndex((s) => s.id === stepId);
  };

  // F-079 — track whether the card is currently shown, so onShown /
  // onDismissed only fire on actual transitions (idempotent).
  let visible = false;
  const setVisible = (next: boolean): void => {
    if (next === visible) return;
    visible = next;
    wrap.style.display = next ? "flex" : "none";
    if (next) options.onShown?.();
    else options.onDismissed?.();
  };

  const render = (): void => {
    const step = controller.currentStep();
    if (!step) {
      setVisible(false);
      return;
    }
    const idx = findStepIndex(step.id);
    title.textContent = step.title;
    body.textContent = step.body;
    stepCount.textContent = idx >= 0 ? `${idx + 1} / ${totalSteps}` : "";
    // Last step → CTA reads "시작!" instead of "다음" so the player
    // knows clicking it ends the tutorial and starts the game.
    next.textContent =
      idx === totalSteps - 1 ? t("tutorial.start") : t("tutorial.next");
    setVisible(true);
  };

  next.addEventListener("click", () => {
    if (controller.next() === null) {
      setVisible(false);
    } else {
      render();
    }
  });
  skip.addEventListener("click", () => {
    controller.skip();
    setVisible(false);
  });

  return { show: render };
}
